import { z } from 'zod';
import { equipmentKindSchema, idSchema, unitKeySchema } from './common.js';
import { mealPlanKindSchema, planAttendeeDtoSchema, planAttendeesInputSchema, planWarningSchema, } from './profiles.js';
/** Meal plans, combined cooking timeline (PLAN §4.1) and suggestions (§4.2) — Phase 2. */
export const MEAL_SLOTS = ['breakfast', 'lunch', 'dinner', 'snack'];
export const mealSlotSchema = z.enum(MEAL_SLOTS);
export const MEAL_PLAN_STATUSES = ['planned', 'shopped', 'cooked', 'skipped'];
export const mealPlanStatusSchema = z.enum(MEAL_PLAN_STATUSES);
export const dateOnlySchema = z.iso.date(); // YYYY-MM-DD
/** Local wall-clock time HH:MM (household timezone). */
export const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');
export const planRecipeDtoSchema = z.object({
    recipeId: idSchema,
    title: z.string(),
    slug: z.string(),
    photoMediaId: idSchema.nullable(),
    servings: z.number().int().positive().nullable(),
    prepMin: z.number().int().nonnegative().nullable(),
    cookMin: z.number().int().nonnegative().nullable(),
    scale: z.number().positive(),
    colorIndex: z.number().int().nonnegative(),
    /**
     * Set when this recipe is the household's fork of a shared recipe. Adding
     * another household's public recipe to a plan links the caller's fork
     * (fork-on-use, PLAN §9 item 13): match the id you sent against this field.
     */
    forkedFromRecipeId: idSchema.nullable().optional(),
});
export const mealPlanDtoSchema = z.object({
    id: idSchema,
    date: dateOnlySchema,
    slot: mealSlotSchema,
    serveAt: timeOfDaySchema.nullable(),
    servings: z.number().int().positive(),
    status: mealPlanStatusSchema,
    /**
     * Leftovers meal (PLAN §9 #7): the plan whose extra servings this meal eats.
     * Such a plan has no recipes of its own (`recipes` is always empty), never
     * adds to grocery lists, has no timeline and is skipped by suggestions.
     * Null for an ordinary meal, or when the source plan was deleted.
     */
    leftoversOfPlanId: idSchema.nullable().default(null),
    /** Display summary of the source plan (its recipe titles joined), or null. */
    leftoversOf: z
        .object({
        planId: idSchema,
        title: z.string(),
        date: dateOnlySchema,
        slot: mealSlotSchema,
    })
        .nullable()
        // Defaults keep older cached payloads (mobile offline cache) parseable.
        .default(null),
    recipes: z.array(planRecipeDtoSchema),
    /**
     * D32. `dine_out` meals have no recipes, timeline or grocery lines;
     * `packed` meals are eaten at `placeId`. Defaults keep older cached
     * payloads (mobile offline cache) parseable.
     */
    kind: mealPlanKindSchema.default('cook'),
    /** Where the meal is eaten; null = home. */
    placeId: idSchema.nullable().default(null),
    placeName: z.string().nullable().default(null),
    dineOutNote: z.string().nullable().default(null),
    /** Planner cost estimate at accept time, cents. */
    estCostCents: z.number().int().nullable().default(null),
    /** Who eats it; empty = the whole household. */
    attendees: z.array(planAttendeeDtoSchema).default([]),
    /** Dislike/allergen/diet warnings for the attendees, computed on read. */
    warnings: z.array(planWarningSchema).default([]),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const planRecipeInputSchema = z.object({
    recipeId: idSchema,
    /** Multiplier applied to the recipe's own servings; default = plan servings / recipe servings. */
    scale: z.number().positive().max(20).optional(),
});
/** POST /api/v1/plans */
export const createMealPlanRequestSchema = z.object({
    date: dateOnlySchema,
    slot: mealSlotSchema.default('dinner'),
    serveAt: timeOfDaySchema.nullable().optional(),
    /** Defaults to the attendee count when attendees are given, else the household's default_servings. */
    servings: z.number().int().min(1).max(50).optional(),
    recipes: z.array(planRecipeInputSchema).max(12).default([]),
    /** D32: default `cook`. `dine_out` takes no recipes (400 `DINE_OUT_HAS_NO_RECIPES`). */
    kind: z.enum(['cook', 'dine_out', 'packed']).optional(),
    /** A household meal place (404 `PLACE_NOT_FOUND` otherwise); null = home. */
    placeId: idSchema.nullable().optional(),
    dineOutNote: z.string().trim().max(200).nullable().optional(),
    estCostCents: z.number().int().min(0).max(10_000_000).nullable().optional(),
    /** Omitted = the whole household (no attendee rows). */
    attendees: planAttendeesInputSchema.optional(),
});
/**
 * PATCH /api/v1/plans/:id. Changing `servings` rescales every recipe in the
 * plan by new/old. `status: 'cooked'` is rejected (400
 * `PLAN_STATUS_VIA_COOKED`): use POST /plans/:id/cooked so the pantry is
 * decremented. For a leftovers meal only date/slot/serveAt/status apply.
 */
export const updateMealPlanRequestSchema = z
    .object({
    date: dateOnlySchema,
    slot: mealSlotSchema,
    serveAt: timeOfDaySchema.nullable(),
    servings: z.number().int().min(1).max(50),
    status: mealPlanStatusSchema,
    /** D32. Switching to `dine_out` needs a plan without recipes (409 `PLAN_HAS_RECIPES`). */
    kind: z.enum(['cook', 'dine_out', 'packed']),
    placeId: idSchema.nullable(),
    dineOutNote: z.string().trim().max(200).nullable(),
})
    .partial();
/**
 * POST /api/v1/plans/:id/leftovers → creates a leftovers meal on `date`/`slot`
 * linked to plan `:id` (201, the new plan). `servings` defaults to the extra
 * servings the source makes beyond its own plan servings (at least 1).
 * 409 `LEFTOVERS_OF_LEFTOVERS` when `:id` is itself a leftovers meal.
 */
export const createLeftoversRequestSchema = z.object({
    date: dateOnlySchema,
    slot: mealSlotSchema,
    serveAt: timeOfDaySchema.nullable().optional(),
    servings: z.number().int().min(1).max(50).optional(),
});
/** POST /api/v1/plans/:id/recipes, PATCH /api/v1/plans/:id/recipes/:recipeId {scale}, DELETE … */
export const addPlanRecipeRequestSchema = planRecipeInputSchema;
export const updatePlanRecipeRequestSchema = z.object({ scale: z.number().positive().max(20) });
/** GET /api/v1/plans?from=&to= (inclusive dates, max 62 days) */
export const planListQuerySchema = z.object({ from: dateOnlySchema, to: dateOnlySchema });
export const planListResponseSchema = z.object({ plans: z.array(mealPlanDtoSchema) });
// ---------------------------------------------------------------- timeline --
export const timelineEquipmentSchema = z.object({
    kind: equipmentKindSchema,
    count: z.number().int().positive().default(1),
    ovenTempF: z.number().int().optional(),
    ovenMode: z.string().optional(),
});
export const timelineEntrySchema = z.object({
    recipeId: idSchema,
    colorIndex: z.number().int().nonnegative(),
    stepId: idSchema,
    sort: z.number().int(),
    text: z.string(),
    start: z.iso.datetime(),
    end: z.iso.datetime(),
    durationMin: z.number().nonnegative(),
    attended: z.boolean(),
    timer: z.boolean(),
    doAhead: z.boolean(),
    equipment: timelineEquipmentSchema.nullable(),
    /** Ingredient names this step uses (from step.ingredientRefs), for the cooking view. */
    ingredients: z.array(z.string()).default([]),
});
export const timelineConflictSchema = z.object({
    kind: z.enum(['attention', 'equipment', 'oven_temp', 'too_late']),
    at: z.iso.datetime(),
    message: z.string(),
    recipeIds: z.array(idSchema),
    suggestion: z.string().optional(),
});
/** GET /api/v1/plans/:id/timeline?serveAt=&now= (ISO datetimes; serveAt overrides the plan's) */
export const timelineQuerySchema = z.object({
    serveAt: z.iso.datetime().optional(),
    now: z.iso.datetime().optional(),
});
export const timelineResponseSchema = z.object({
    plan: mealPlanDtoSchema,
    serveAt: z.iso.datetime(),
    startAt: z.iso.datetime(),
    totalActiveMin: z.number().nonnegative(),
    feasible: z.boolean(),
    conflicts: z.array(timelineConflictSchema),
    legend: z.array(z.object({ recipeId: idSchema, title: z.string(), colorIndex: z.number().int() })),
    entries: z.array(timelineEntrySchema),
});
// ------------------------------------------------------------ mark cooked --
/** GET /api/v1/plans/:id/cooked-preview → what the pantry decrement would consume (D10) */
export const cookedProposalSchema = z.object({
    pantryItemId: idSchema,
    ingredientId: idSchema,
    ingredientName: z.string(),
    /** Quantity the plan needs (scaled), in the pantry item's unit when convertible. */
    needQty: z.number().nonnegative().nullable(),
    availableQty: z.number().nonnegative(),
    unit: unitKeySchema,
    /** Proposed amount to subtract (≤ available); null = not convertible, user decides. */
    consumeQty: z.number().nonnegative().nullable(),
    note: z.string().optional(),
    /** The linked product's label ("Black beans, 15 oz can"), when the pantry row has one. */
    productLabel: z.string().nullable(),
    /** Default for "discard the rest" (D35 F3): the opened remainder will not keep. */
    suggestDiscard: z.boolean(),
    /** Why `suggestDiscard` is true ("Opened cans don't keep"); null otherwise. */
    discardReason: z.string().nullable(),
});
export const cookedPreviewResponseSchema = z.object({
    proposals: z.array(cookedProposalSchema),
    /** Ingredients the plan needs that are not in the pantry at all. */
    missing: z.array(z.object({ ingredientId: idSchema, ingredientName: z.string() })),
});
/** POST /api/v1/plans/:id/cooked — the confirmed list; empty array = mark cooked without touching the pantry */
export const markCookedRequestSchema = z.object({
    consume: z
        .array(z.object({
        pantryItemId: idSchema,
        qty: z.number().positive(),
        unit: unitKeySchema,
        /**
         * Throw away what is left after `qty` (D35 F3): the whole row is used
         * up and the remainder is recorded as waste in the audit entry.
         */
        discardRemainder: z.boolean().default(false),
    }))
        .max(100)
        .default([]),
});
// ------------------------------------------------------------- suggestions --
/** GET /api/v1/plans/suggestions?date=&slot=&limit=&windowDays= */
export const suggestionsQuerySchema = z.object({
    date: dateOnlySchema,
    slot: mealSlotSchema.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(10),
    /** Plan window ± days around `date` used for ingredient overlap. Default 3. */
    windowDays: z.coerce.number().int().min(0).max(14).default(3),
    /** Include public library recipes (default true) as well as the household's own. */
    includeLibrary: z.enum(['true', 'false']).default('true'),
});
export const suggestionReasonSchema = z.object({
    kind: z.enum([
        'overlap',
        'leftover',
        'expiring',
        'new_perishable',
        'repeat',
        'recency',
        'rating',
    ]),
    ingredientId: idSchema.optional(),
    ingredientName: z.string().optional(),
    /** Human explanation: "uses cheddar already on the list". */
    text: z.string(),
    /** Signed contribution to the score. */
    delta: z.number(),
});
export const suggestionDtoSchema = z.object({
    recipeId: idSchema,
    title: z.string(),
    slug: z.string(),
    photoMediaId: idSchema.nullable(),
    own: z.boolean(),
    score: z.number(),
    reasons: z.array(suggestionReasonSchema),
});
export const suggestionsResponseSchema = z.object({ suggestions: z.array(suggestionDtoSchema) });
//# sourceMappingURL=plans.js.map